/**
 * Feature 14 — Unclaimed company profile rendered at /v/:slug.
 *
 * Spec §1346-1351 — NO score, NO risk label, NO trust labels, NO
 * operational data, NO comparative language.  This file is intentionally
 * sparse for that reason.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { directoryApi } from '../services/directory';
import ScoreDisclaimer from './ScoreDisclaimer';


interface UnclaimedCompany {
  name: string;
  city: string;
  state: string;
  website: string;
  phone: string;
  business_type: string;
  year_founded: number | null;
  description: string;
  slug: string;
}


function ClaimFormModal({
  slug, vendorName, onClose,
}: { slug: string; vendorName: string; onClose: () => void }) {
  const [form, setForm] = useState({
    claimant_name: '',
    claimant_email: '',
    claimant_title: '',
    justification: '',
  });
  const mutation = useMutation({
    mutationFn: () => directoryApi.submitClaim({ slug, ...form }),
    onSuccess: () => {
      onClose();
    },
  });
  const disabled = mutation.isPending || !form.claimant_name.trim() || !form.claimant_email.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-slate-900">Claim {vendorName}</h2>
        <p className="mt-1 text-xs text-slate-500">
          A real representative of this company can take ownership of the
          profile. An admin reviews every claim.
        </p>
        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Your name</span>
            <input
              value={form.claimant_name}
              onChange={(e) => setForm({ ...form, claimant_name: e.target.value })}
              className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Email</span>
            <input
              type="email"
              value={form.claimant_email}
              onChange={(e) => setForm({ ...form, claimant_email: e.target.value })}
              className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Title (e.g. Owner, Ops Manager)</span>
            <input
              value={form.claimant_title}
              onChange={(e) => setForm({ ...form, claimant_title: e.target.value })}
              className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">
              How are you affiliated with this company?
            </span>
            <textarea
              rows={3}
              value={form.justification}
              onChange={(e) => setForm({ ...form, justification: e.target.value })}
              className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </label>
          {mutation.isError && (
            <p className="text-xs text-red-600">
              Could not submit. Please try again later.
            </p>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-lg text-slate-700 hover:bg-slate-100">
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={disabled}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-brand-500 text-white disabled:opacity-50 hover:bg-brand-600"
          >
            {mutation.isPending ? 'Submitting…' : 'Submit claim'}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function UnclaimedCompanyProfile({
  company, canClaim,
}: { company: UnclaimedCompany; canClaim: boolean }) {
  const [showClaim, setShowClaim] = useState(false);
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="text-3xl font-semibold text-slate-900">{company.name}</h1>
        <div className="mt-2 text-sm text-slate-500">
          {company.city}, {company.state}
        </div>
        <div className="mt-4 inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
          Unclaimed Profile
        </div>
        <p className="mt-6 text-slate-600">
          GuardGrade participation has not yet been established for this vendor.
        </p>

        <dl className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-left bg-white border border-slate-200 rounded-2xl p-6">
          {company.website && (
            <div>
              <dt className="text-xs text-slate-400">Website</dt>
              <dd>
                <a className="text-brand-600 hover:underline" href={company.website} target="_blank" rel="noopener noreferrer">
                  {company.website}
                </a>
              </dd>
            </div>
          )}
          {company.phone && (
            <div>
              <dt className="text-xs text-slate-400">Phone</dt>
              <dd className="text-slate-700">{company.phone}</dd>
            </div>
          )}
          {company.business_type && (
            <div>
              <dt className="text-xs text-slate-400">Type</dt>
              <dd className="text-slate-700">{company.business_type}</dd>
            </div>
          )}
          {company.year_founded && (
            <div>
              <dt className="text-xs text-slate-400">Founded</dt>
              <dd className="text-slate-700">{company.year_founded}</dd>
            </div>
          )}
          {company.description && (
            <div className="sm:col-span-2">
              <dt className="text-xs text-slate-400">Description</dt>
              <dd className="text-slate-700">{company.description}</dd>
            </div>
          )}
        </dl>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {canClaim && (
            <button
              onClick={() => setShowClaim(true)}
              className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
            >
              Claim this profile
            </button>
          )}
          <Link
            to="/signup"
            className="rounded-xl border-2 border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Learn About GuardGrade
          </Link>
        </div>

        {/* Feature 10: short disclaimer with Read more modal */}
        <div className="mt-12 text-left">
          <ScoreDisclaimer variant="short" />
        </div>
      </div>

      {showClaim && (
        <ClaimFormModal
          slug={company.slug}
          vendorName={company.name}
          onClose={() => setShowClaim(false)}
        />
      )}
    </div>
  );
}
